import { useTranslation } from 'react-i18next';
import Skeleton from '@/components/base/Skeleton';
import ErrorState from '@/components/base/ErrorState';
import { formatCurrency } from '@/lib/format';
import type { DashboardSummary } from '@/types';

interface SummaryCardsProps {
  data: DashboardSummary | null;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
}

const TONES: Record<string, string> = {
  amber: 'bg-amber-50 text-amber-600',
  accent: 'bg-accent-100 text-accent-600',
  emerald: 'bg-emerald-50 text-emerald-600',
  primary: 'bg-primary-100 text-primary-700',
};

export default function SummaryCards({ data, loading, error, onRetry }: SummaryCardsProps) {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="mt-3 h-8 w-16" />
            <Skeleton className="mt-2 h-3 w-32" />
          </div>
        ))}
      </div>
    );
  }

  if (error || !data) {
    return (
      <ErrorState
        title={t('home.summaryError')}
        description={t('common.errorText')}
        retryLabel={t('common.retry')}
        onRetry={onRetry}
      />
    );
  }

  const cards = [
    {
      key: 'new',
      label: t('home.cards.new'),
      value: String(data.newOrders),
      hint: t('home.cards.newHint'),
      icon: 'ri-file-add-line',
      tone: TONES.amber,
    },
    {
      key: 'inProgress',
      label: t('home.cards.inProgress'),
      value: String(data.inProgress),
      hint: t('home.cards.inProgressHint'),
      icon: 'ri-loader-4-line',
      tone: TONES.accent,
    },
    {
      key: 'completedToday',
      label: t('home.cards.completedToday'),
      value: String(data.completedToday),
      hint: t('home.cards.completedHint'),
      icon: 'ri-checkbox-circle-line',
      tone: TONES.emerald,
    },
    {
      key: 'revenue',
      label: t('home.cards.revenue'),
      value: formatCurrency(data.revenueToday, data.currency),
      hint: t('home.cards.revenueHint'),
      icon: 'ri-wallet-3-line',
      tone: TONES.primary,
    },
  ];

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
      {cards.map((card) => (
        <div
          key={card.key}
          className="rounded-lg border border-background-200 bg-background-50 p-4 shadow-xs transition-all duration-200 ease-smooth hover:-translate-y-0.5 hover:border-background-300 hover:shadow-card md:p-5 animate-fade-in-up"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm text-foreground-500">{card.label}</p>
              <p className="mt-2 text-2xl font-bold text-foreground-950">{card.value}</p>
              <p className="mt-1 text-xs leading-relaxed text-foreground-400">{card.hint}</p>
            </div>
            <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-md ${card.tone}`}>
              <i className={`${card.icon} text-xl leading-none`} aria-hidden="true" />
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}